import React, { useEffect, useRef, useState } from "react";
import { Card, Form, Spinner, Col, Row } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  generateTaskHtml,
  filterSubtasksMap,
  handleSendProposal,
  fetchTemplateSections,
  EmailConfigRequiredModal,
  getTranslatedSalutation
} from "../../../assets/js/common";
import axios from "axios";
import "assets/css/template.css";
import "@fortawesome/fontawesome-free/css/all.min.css"; // Import Font Awesome
import EditableSectionModal from "../proposalModals/pditableSectionModal";
import IconButton from "components/common/iconButton";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { handleUploadPDF } from "components/proposals/proposalPdfMethods/handleProposalPdf";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getDomainConfig } from "../../../config/domainConfig";

const TemplateRenderer = ({ formData, workType, logoSrc }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isSavingRef = useRef(false);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [sectionMap, setSectionMap] = useState({});
  const [values, setValues] = useState({});
  const [proposalTaskData, setProposalTaskData] = useState([]);
  const [task, setTask] = useState([]); // assume you’ll fetch this
  const [subtasksMap, setSubtasksMap] = useState({});
  const [templateTaskContent, setTemplateTaskContent] = useState("");
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingKey, setEditingKey] = useState(""); // which field is being edited
  const [editorValue, setEditorValue] = useState(""); // current content in modal
  const invoiceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [borderVisible, setBorderVisible] = useState(true); // if needed
  const [showEmailConfigRequiredModal, setShowEmailConfigRequiredModal] = useState(false);
  const [emailModalType, setEmailModalType] = useState("email");
  const { financialLabels: finLabels } = getDomainConfig();
  const isEditMode = !!formData?.proposalDetailsData;

  // All currency symbols to detect in template text
  const CURRENCY_SYMBOLS = ["€", "₹", "$", "£", "¥"];

  // Price-related keywords in EN + NL
  const PRICE_KEYWORDS = [
    "price", "cost", "amount", "rate", "fee", "charge",
    "prijs", "bedrag", "tarief", "kosten", "vergoeding",
  ];

  const stripHtml = (html) => html.replace(/<[^>]*>/g, "");

  const formatCost = (cost, currencySymbol, currencyLocale) => {
    if (cost == null) return "";

    return (
      currencySymbol +
      Number(cost).toLocaleString(currencyLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // ── main injection function ──────────────────────────────────────────────
  const originalPriceSectionRef = useRef("");
  const previousPriceDataRef = useRef({
    cost: null,
    jobFrequency: null,
    jobFrequencyCount: null,
    billingCycle: null,
  });

  useEffect(() => {
    if (!sectionMap["priceSection"]) return;

    const { cost, jobFrequency, jobFrequencyCount, billingCycle } = formData || {};

    if (cost == null && !jobFrequency && !billingCycle) return;

    const currentPriceData = { cost, jobFrequency, jobFrequencyCount, billingCycle };

    const previousPriceData = previousPriceDataRef.current;

    const isSame =
      previousPriceData.cost === currentPriceData.cost &&
      previousPriceData.jobFrequency === currentPriceData.jobFrequency &&
      previousPriceData.jobFrequencyCount === currentPriceData.jobFrequencyCount &&
      previousPriceData.billingCycle === currentPriceData.billingCycle;

    if (isSame) return;
    previousPriceDataRef.current = currentPriceData;

    setValues((prev) => {
      const baseHtml = originalPriceSectionRef.current || prev["priceSection"] || "";

      const updated = injectPriceIntoSection(
        baseHtml,
        cost,
        jobFrequency,
        jobFrequencyCount,
        billingCycle,
        finLabels.currencySymbol,
        t
      );

      if (updated === prev["priceSection"]) return prev;
      return { ...prev, priceSection: updated };
    });

  }, [
    formData?.cost,
    formData?.jobFrequency,
    formData?.jobFrequencyCount,
    formData?.billingCycle,
    sectionMap,
    t
  ]);

  const injectPriceIntoSection = (current, cost, jobFrequency, jobFrequencyCount, billingCycle, currencySymbol, t) => {
    const costStr = formatCost(
      cost,
      finLabels.currencySymbol,
      finLabels.currencyLocale
    );

    const isOneTimeCycle = billingCycle === "OneTime";
    const isOneTimeFrequency = jobFrequency === "OneTime";

    const cycleStr = billingCycle
      ? t(`billingCycleLabel.${billingCycle}`, { defaultValue: billingCycle })
      : "";

    const freqStr = !jobFrequency
      ? ""
      : isOneTimeCycle
        // billing cycle already reads "One Time" — don't repeat it via frequency
        ? ""
        : isOneTimeFrequency
          ? t(`jobFrequencyLabel.${jobFrequency}`, { defaultValue: jobFrequency })
          : jobFrequencyCount
            ? `${jobFrequencyCount}× ${t(`jobFrequencyLabel.${jobFrequency}`, { defaultValue: jobFrequency })}`
            : t(`jobFrequencyLabel.${jobFrequency}`, { defaultValue: jobFrequency });

    const combined = [costStr, cycleStr, freqStr].filter(Boolean).join(", ");

    if (!combined) return current;

    const normalizedCurrent = current
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/g, " ");

    // ── CASE 1 check previously injected data
    if (normalizedCurrent.includes('data-price-injected="true"')) {
      const updatedHtml = normalizedCurrent.replace(
        /<span[^>]*data-price-injected="true"[^>]*>[\s\S]*?<\/span>|<p[^>]*data-price-injected="true"[^>]*>[\s\S]*?<\/p>|<strong[^>]*data-price-injected="true"[^>]*>[\s\S]*?<\/strong>/i,
        `<span data-price-injected="true">${combined}</span>`
      );
      return updatedHtml;
    }

    const plain = stripHtml(normalizedCurrent);

    // ── CASE 2: explicit placeholder ──────────────────────────────────
    const pricePlaceholderRe =
      /\{\{[^}]*?(price|cost|prijs|tarief)[^}]*?\}\}|\[(price|cost|prijs|tarief)\]/gi;

    const frequencyPlaceholderRe =
      /\{\{[^}]*?(frequency|freq|frequentie)[^}]*?\}\}|\[(frequency|freq|frequentie)\]/gi;

    // NEW: billingCycle placeholder
    const billingCyclePlaceholderRe =
      /\{\{[^}]*?(billingCycle|facturatiecyclus|cyclus)[^}]*?\}\}/gi;

    const hasPlaceholders =
      pricePlaceholderRe.test(current) ||
      frequencyPlaceholderRe.test(current) ||
      billingCyclePlaceholderRe.test(current);  // ADD

    if (hasPlaceholders) {
      const updatedHtml = current
        .replace(pricePlaceholderRe, costStr)
        .replace(frequencyPlaceholderRe, freqStr)
        .replace(billingCyclePlaceholderRe, cycleStr);  // ADD

      return updatedHtml;
    }

    // ── CASE 3: currency symbol + number found ────────────────────────
    const detectedSymbol = CURRENCY_SYMBOLS.find((s) => plain.includes(s));
    if (detectedSymbol) {
      const sym = escRe(detectedSymbol);

      const fullExprPattern =
        `${sym}\\s*[\\d.,]+[^.<]*`;

      const allMatches = [
        ...normalizedCurrent.matchAll(new RegExp(fullExprPattern, "gi"))
      ];

      if (allMatches.length > 0) {
        const updatedHtml = normalizedCurrent.replace(
          new RegExp(fullExprPattern, "i"),
          `<span data-price-injected="true">${combined}</span>`
        );
        return updatedHtml;
      }

      const symbolOnlyRe = new RegExp(`(${sym})`, "g");
      return normalizedCurrent.replace(
        symbolOnlyRe,
        `<span data-price-injected="true">$1\u00A0${[costStr.replace(detectedSymbol, "").trim(), cycleStr, freqStr].filter(Boolean).join(", ")}</span>`
      );
    }

    // ── CASE 4: price keyword, no currency symbol ─────────────────────
    const keywordPattern = PRICE_KEYWORDS.map(escRe).join("|");

    if (new RegExp(`\\b(${keywordPattern})\\b`, "gi").test(plain)) {
      const htmlKeywordRe = new RegExp(
        `(\\b(?:${keywordPattern})\\b)`,
        "gi"
      );

      let injected = false;

      const updatedHtml = current.replace(
        htmlKeywordRe,
        (match) => {
          if (injected) return match;
          injected = true;
          return `${match} <span data-price-injected="true">${combined}</span>`;
        }
      );

      return updatedHtml;
    }

    // ── CASE 5: text exists but no pricing context ────────────────────
    if (plain.trim()) {
      return `<p data-price-injected="true">${combined}</p>` + current;
    }
    return `<p data-price-injected="true">${combined}</p>`;

  };

  useEffect(() => {
    const section = sectionMap["priceSection"];
    if (!section) return;

    if (section.value && !originalPriceSectionRef.current) {
      originalPriceSectionRef.current = section.value;
    }

  }, [sectionMap]);

  useEffect(() => {
    if (!formData?.customerId || !formData?.workType) return;

    fetchTemplateSections(
      formData?.customerId,
      formData?.templateId,
      formData?.workType,
      setTemplateConfig,
      setSectionMap,
      setValues,
      formData?.proposalDetailsData,   // already existing
      isEditMode ? formData?.proposalId : null, // pass proposalId if editing
      formData?.proposalVersionId || formData?.currentVersionId
    );
  }, [formData?.customerId, formData?.workType, formData?.proposalId]);

  useEffect(() => {
    const preloaded = formData?.taskWithSubtasks;
    if (Array.isArray(preloaded) && preloaded.length > 0) {
      setProposalTaskData(preloaded);
      setTask(preloaded.map((t) => ({ TaskId: t.TaskId, TaskName: t.label })));
      setSubtasksMap(
        preloaded.reduce((map, t) => {
          map[t.TaskId] = t.Subtasks || [];
          return map;
        }, {})
      );
      return;
    }

    fetchProposalTask(formData?.proposalId);
  }, [formData?.proposalId, formData?.taskWithSubtasks]);

  const fetchProposalTask = async (proposalId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setProposalTaskData(response.data);
    } catch (error) {
      console.error("Error fetching proposal task data:", error);
    }
  };

  useEffect(() => {
    if (Array.isArray(formData?.taskWithSubtasks) && formData.taskWithSubtasks.length > 0) {
      return;
    }

    const fetchAllSubtasks = async () => {
      const results = await Promise.all(
        proposalTaskData.map(async (task) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
            );
            return [task.TaskId, response.data];
          } catch (error) {
            console.error(
              `Error fetching subtasks for task ${task.TaskId}`,
              error
            );
            return null;
          }
        })
      );

      const allSubtasks = {};
      results.filter(Boolean).forEach(([taskId, subtasks]) => {
        allSubtasks[taskId] = subtasks;
      });
      setSubtasksMap(allSubtasks);
    };

    if (proposalTaskData && proposalTaskData.length > 0) {
      fetchTask();
      fetchAllSubtasks();
    }
  }, [proposalTaskData, formData?.taskWithSubtasks]);

  const fetchTask = async () => {
    const customerId = formData?.customerId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/taskRoute/tasks`,
        {
          params: { customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setTask(response.data);
    } catch (error) {
      console.error("Error fetching task data:", error);
    }
  };

  useEffect(() => {
    if (proposalTaskData && proposalTaskData.length > 0 && task.length > 0) {

      const selectedSubtasks = (Array.isArray(formData.selectedSubtasks)
        ? formData.selectedSubtasks
        : []
      ).filter((s) => s.selected !== false);

      const filteredMap = filterSubtasksMap(subtasksMap, selectedSubtasks);

      const tasksHtml = generateTaskHtml(
        proposalTaskData,
        task,
        filteredMap,
        selectedSubtasks,
        t,
        formData?.templateName
      );

      setTemplateTaskContent(tasksHtml);
    }
  }, [
    proposalTaskData,
    task,
    subtasksMap,
    formData.selectedSubtasks,
    t,
    workType,
    formData?.templateName
  ]);

  const renderSection = (key) => {
    const section = sectionMap[key];
    if (!section) return null;

    if (section.type === "dynamic") {
      switch (section.source) {
        case "introTextBlock":
          const introText = t(`introTextBlock.introText`, {
            StreetName: formData?.selectedClientData?.StreetName || "",
            PostCode: formData?.selectedClientData?.PostCode || "",
            City: formData?.selectedClientData?.City || "",
          });

          return (
            <p key={key} className="fs-10 mb-3">
              {introText}
            </p>
          );

        case "timeScheduleText": {
          const { startDate, endDate, startTime } = formData || {};
          if (!startDate) return null;

          const formattedStartDate = moment(startDate).format("DD-MM-YYYY");

          const formattedStartTime = startTime && startDate
            ? moment
              .utc(`${moment(startDate).format("YYYY-MM-DD")}T${moment.utc(startTime).format("HH:mm:ss")}`)
              .local()
              .format("HH:mm")
            : "";

          const formattedEndDate = endDate
            ? moment(endDate).format("DD-MM-YYYY")
            : "";

          let timeText = "";

          if (startTime && endDate) {
            timeText = t("timeScheduleTextWithEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
              EndDate: formattedEndDate,
            });
          } else if (startTime) {
            timeText = t("timeScheduleTextWithoutEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
            });
          } else {
            timeText = t("timeScheduleTextOnlyDate", {
              StartDate: formattedStartDate,
            });
          }

          return (
            <p key={key} className="fs-10 mb-3">
              {timeText}
            </p>
          );
        }

        case "proposalTasksBlock":
          if (!templateTaskContent?.trim()) return null;
          return (
            <div className="fs-10 mb-3" key={key}>
              <span dangerouslySetInnerHTML={{ __html: templateTaskContent }} />
            </div>
          );

        case "pageBreakAfterExtraContent":
          return (
            <div
              className="text-muted text-center"
              style={{
                margin: "40px 0",
                borderTop: "2px dashed #aaa",
                position: "relative",
                fontSize: "12px",
                color: "#777",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "-10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#fff",
                  padding: "0 8px",
                }}
              >
                Page Break
              </span>
            </div>
          );
        default:
          return null;
      }
    }

    if (section.type === "editable") {
      const isEmpty =
        !values[section.key] ||
        values[section.key].replace(/<[^>]*>/g, "").trim() === "";

      const { showIfsc } = getDomainConfig().financialLabels;
      const isAcceptance = section.key === "acceptanceSection";
      const resolvedContent = values[section.key];
      const effectivelyEmpty = !resolvedContent || resolvedContent.replace(/<[^>]*>/g, "").trim() === "";

      //  Skip rendering completely if it's empty and not in edit mode (e.g., during PDF)
      if (!borderVisible && isEmpty) return null;

      return (
        <div key={section.key} className="mb-3">
          {section.label_key && (
            <p className="mb-1 fs-10">
              <strong>{t(section.label_key)}:</strong>
              <br />
            </p>
          )}

          <div
            className={`${borderVisible ? "borderVisible" : ""
              } commonStyles fs-10`}
            onClick={() =>
              borderVisible ? handleOpenEditor(section.key) : undefined
            }
          >
            {borderVisible && (
              <div className="pencilIconContainer">
                <i className="fas fa-pencil-alt pencilIcon" />
              </div>
            )}

            <div
              id={section.key}
              contentEditable={false}
              suppressContentEditableWarning={true}
              dangerouslySetInnerHTML={{
                __html: !effectivelyEmpty
                  ? resolvedContent
                  : borderVisible
                    ? t("clickToAddExtraContent")
                    : "",
              }}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  if (!templateConfig || !Object.keys(sectionMap).length) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const renderLine = (text) => {
    if (!text) return null;

    let cleaned = String(text).trim();

    // remove leading hyphens like "-- ", "--- ", etc.
    cleaned = cleaned.replace(/^[-\s]+/, "");

    // remove placeholders or empty lines
    if (!cleaned) return null;

    return (
      <>
        {cleaned}
        <br />
      </>
    );
  };

  const handleOpenEditor = (key) => {
    setEditingKey(key);
    setEditorValue(values[key] || "");
    setEditorVisible(true);
  };

  const handleCloseEditor = () => {
    setEditorVisible(false);
    setEditingKey("");
  };

  const handleSaveEditor = (updatedValue) => {
    setValues((prev) => ({
      ...prev,
      [editingKey]: updatedValue,
    }));
    handleCloseEditor();
  };

  const updateProposalStatus = async (status, comment) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalRoute/updateProposal-status/${formData.proposalId}`,
        { Status: status, Comment: comment },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
    } catch (error) {
      console.error("Proposal Status Error:", error);
    }
  };

  const handlePreviewPDF = async (invoiceRef, setBorderVisible) => {
    setBorderVisible(false);

    await new Promise((resolve) => setTimeout(resolve, 100));

    const input = invoiceRef.current;
    if (!input) {
      console.error("Invoice element not found");
      return;
    }

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const padding = 10;

      const cards = input.querySelectorAll(".card");

      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const wrapper = document.createElement("div");

        const style = document.createElement("style");
        style.innerHTML = `
            * {
                font-family: Arial, sans-serif !important;
                font-size: 13px !important;
                line-height: 1.4 !important;
              }
              .fs-10 {
                font-size: 13px !important;
              }
              img {
                max-width: 100%;
                height: auto;
                object-fit: contain;
              }
    `;
        wrapper.appendChild(style);

        wrapper.style.position = "absolute";
        wrapper.style.left = "-9999px";
        wrapper.style.top = "0";
        wrapper.style.width = `${card.clientWidth}px`;
        wrapper.style.background = "white";
        wrapper.style.padding = "10px";

        wrapper.appendChild(card.cloneNode(true));
        document.body.appendChild(wrapper);

        const canvas = await html2canvas(wrapper, {
          scale: 1.7,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff"
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.7);

        document.body.removeChild(wrapper);

        const usableWidth = pdfWidth - padding * 2;
        const usableHeight = pdfHeight - padding * 2;
        const imgRatio = canvas.width / canvas.height;
        const pdfRatio = usableWidth / usableHeight;

        let imgWidth, imgHeight;
        if (imgRatio > pdfRatio) {
          imgWidth = usableWidth;
          imgHeight = usableWidth / imgRatio;
        } else {
          imgHeight = usableHeight;
          imgWidth = usableHeight * imgRatio;
        }

        const x = (pdfWidth - imgWidth) / 2;
        const y = padding;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      }

      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }

    setBorderVisible(true);
  };

  const getPagesFromSections = () => {
    const pages = [];
    let current = [];

    templateConfig.sections.forEach((sectionKey) => {
      if (sectionKey === "pageBreakAfterExtraContent") {
        pages.push([...current]);
        current = [];
      } else {
        current.push(sectionKey);
      }
    });

    if (current.length > 0) {
      pages.push(current);
    }

    return pages;
  };

  const handleSaveProposalDetails = async (shouldSend = false) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsLoading(true);
    try {
      // ── fire PDF generation immediately — do NOT await here ──────
      const pdfPromise = handleUploadPDF(
        invoiceRef,
        formData,
        formData?.proposalVersionId || formData?.currentVersionId || null,
        setBorderVisible
      )

      const payload = {
        proposalDetailId: formData?.proposalDetailId || null,
        proposalId: formData?.proposalId,
        proposalVersionId: formData?.proposalVersionId || null,
        relationId: formData?.selectedClientData?.RelationId,
        locationId: formData?.locationDetails?.LocationId,
        workType: formData?.workType,
        templateId: formData?.templateId,
        templateName: formData?.templateName,
        communicationUserId: formData?.communicationUserId,
        document: null,                                         // PDF not ready yet
        date: moment().toISOString(),
        comment: formData?.comment || null,
        companySignature: formData?.companySignature || null,
        companySignatureDate: formData?.companySignatureDate || moment().toISOString(),
        clientSignature: formData?.clientSignatureImage || null,
        clientSignatureDate: formData?.clientSignatureDate || null,
        paymentTermsSection: values?.paymentTermsSection || null,
        acceptanceSection: values?.acceptanceSection || null,
        extraContentText: values?.extraContentText || null,
        priceSection: values?.priceSection || null,
        terminationSection: values?.terminationSection || null,
      };

      // ── save proposal details (await — need the returned ID) ─────
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/addUpdateProposalDetails`,
        payload,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      const newProposalDetailId = response.data?.ProposalDetailId || payload.proposalDetailId;

      // ── parallel saves that only depend on the ID above ──────────
      const parallelSaves = [];
      
      const allProposalTaskIds = (formData.taskWithSubtasks || [])
        .map((task) => task.ProposalTaskId)
        .filter(Boolean);

      if (allProposalTaskIds.length > 0 || formData.selectedSubtasks?.length > 0) {
        parallelSaves.push(
          axios.post(
            `${process.env.REACT_APP_API_URL}/proposalRoute/subtasks/replace`,
            {
              proposalId: formData.proposalId,
              proposalVersionId: formData?.proposalVersionId || null,
              proposalTaskIds: allProposalTaskIds,
              subtasks: (formData.selectedSubtasks || []).map((item) => ({
                ProposalTaskId: item.proposalTaskId,
                ...(item.isCustom ? { subtaskName: item.subtaskName, isCustom: true } : { SubtaskId: item.subtaskId }),
                SelectedDays: item.selectedDays,
                MonthlyDay: item.monthlyDay,
                selected: item.selected,
              })),
            },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          )
        );
      }

      if (formData?.templateId && templateConfig?.sections?.length > 0) {
        const templatePayload = templateConfig.sections
          .map((key, index) => {
            const sectionData = sectionMap[key] || {};

            const editedContent =
              values?.[key] !== undefined && values?.[key] !== null
                ? values[key]
                : sectionData.defaultContent || "";

            return {
              ProposalId: formData?.proposalId,
              ProposalVersionId: formData?.proposalVersionId || null,
              CustomerId: formData?.customerId,
              ProposalDetailId: newProposalDetailId,
              SectionId: sectionData.sectionId,   // undefined for orphan keys
              SectionOrder: index + 1,
              DefaultContent: editedContent,
              LanguageCode: sectionData.languageCode || i18n.language,
              WorkType: formData?.workType,
              TemplateId: templateConfig?.templateId,
              TemplateName: formData?.templateName,
            };
          })
          .filter((item) => !!item.SectionId); // ← drop any item with no resolved SectionId

        if (templatePayload.length > 0) {
          parallelSaves.push(
            axios.post(
              `${process.env.REACT_APP_API_URL}/proposalDetailsTemplateRoute/saveProposalTemplate`,
              templatePayload,
              { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            ).catch((error) => {
              console.error("Error saving Proposal Details Template:", error);
              toast.error(t("failedToSaveProposalTemplate"), { position: "top-right", autoClose: 4000 });
            })
          );
        }
      }

      await Promise.all(parallelSaves);

      pdfPromise
        .then(async (pdfBlobUrl) => {
          if (!pdfBlobUrl) return;
          const existingDocument = formData?.proposalDetailsData?.Document;
          if (existingDocument) {
            try {
              await axios.delete(
                `${process.env.REACT_APP_API_URL}/blobRoute/deleteBlob`,
                {
                  params: { blobUrl: existingDocument },
                  headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                }
              );
            } catch (deleteErr) {
              console.warn("Could not delete existing PDF blob:", deleteErr);
            }
          }

          await axios.post(
            `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/addUpdateProposalDetails`,
            { proposalDetailId: newProposalDetailId, document: pdfBlobUrl },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
        })
        .catch((err) => console.error("Background PDF update failed:", err));

      // ── SEND path ─────────────────────────────────────────────────
      if (shouldSend) {
        if (!formData?.companySignature) {
          setEmailModalType("signature");
          setShowEmailConfigRequiredModal(true);
          return;                                               // finally still runs
        }

        // unblock UI immediately
        formData?.setShowModal?.(false);
        navigate("/proposals");

        // fire-and-forget send — do NOT await
        handleSendProposal(
          { ...payload, proposalDetailId: newProposalDetailId, document: null },
          formData,
          formData.selectedClientData,
          t,
          () => { },
          async () => {
            await updateProposalStatus(3, null);
          },
          process.env.REACT_APP_DEFAULT_DOMAIN
        ).catch((err) => console.error("Send proposal failed:", err));

        return;
      }

      // ── SAVE ONLY path ────────────────────────────────────────────
      toast.success(t("proposalDetailsAdded"), { position: "top-right", autoClose: 5000 });
      formData?.setShowModal?.(false);
      navigate("/proposals");

    } catch (error) {
      console.error("Proposal details save error:", error);
      toast.error(t("failedToSaveProposal"));
    } finally {
      setIsLoading(false);
      isSavingRef.current = false;
    }
  };

  return (
    <>
      {/* loader */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255, 255, 255, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">{t("savingProposalPleaseWait")}</p>
          </div>
        </div>
      )}

      {/* rendered template */}

      <div ref={invoiceRef}>
        {getPagesFromSections().map((sectionKeys, pageIndex, allPages) => (
          <Card key={pageIndex} className="mb-3" style={{ marginTop: 0 }}>
            <Card.Body>
              {/* First page: render header */}
              {pageIndex === 0 && (
                <>
                  <Row className="align-items-center text-center mb-3">
                    <Col sm={6} className="text-sm-start">
                      {logoSrc && (
                        <img
                          src={logoSrc}
                          alt="invoice"
                          width={150}
                          style={{
                            border: "none",
                            boxShadow: "none",
                            background: "transparent",
                          }}
                        />
                      )}
                    </Col>
                    <Col className="text-sm-end mt-3 mt-sm-0">
                      {/* Address block */}
                      <p className="fs-10 mb-0">
                        {renderLine(
                          [
                            formData?.settings?.OrganizationName
                          ]
                            .filter(Boolean)
                            .join(" ")
                        )}
                        {renderLine(
                          [
                            formData?.locationDetails?.StreetName,
                            formData?.locationDetails?.HouseNumber,
                          ]
                            .filter(Boolean)
                            .join(" ")
                        )}
                        {renderLine(
                          [
                            formData?.locationDetails?.PostCode,
                            formData?.locationDetails?.City,
                          ]
                            .filter(Boolean)
                            .join(" ")
                        )}
                      </p>
                      <p className="fs-10">
                        {renderLine(formData?.UserPhoneNumber)}
                        {renderLine(formData?.UserEmailaddress)}
                        {formData?.settings?.DomainName !== "app.getplan.nl" &&
                          renderLine(`www.${formData?.settings?.DomainName}`)}
                      </p>
                    </Col>
                  </Row>

                  <Row className="align-items-end">
                    <Col xs={12} sm={8} className="text-sm-start mb-3 mb-sm-0">
                      <p className="fs-10 mb-0">
                        {renderLine(formData?.selectedClientData?.Name)}
                        {renderLine(getTranslatedSalutation(formData?.selectedClientData, t))}
                        {renderLine(
                          [
                            formData?.selectedClientData?.StreetName,
                            formData?.selectedClientData?.PostCode,
                          ]
                            .filter(Boolean)
                            .join(", ")
                        )}
                        {renderLine(
                          [
                            formData?.selectedClientData?.City,
                            formData?.selectedClientData?.Country,
                          ]
                            .filter(Boolean)
                            .join(", ")
                        )}
                        {renderLine(
                          formData?.selectedClientData?.Emailaddress1
                        )}
                      </p>
                    </Col>
                    <Col xs={12} sm={4} className="text-sm-end mb-3 mb-sm-0">
                      <p className="fs-10 mb-0">
                        {t("date")},{" "}
                        {formData?.proposalDetailsData?.Date
                          ? moment(formData.proposalDetailsData?.Date).format(
                            "DD-MM-YYYY"
                          )
                          : moment().format("DD-MM-YYYY")}
                      </p>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <p className="fs-10 mb-2">
                      {formData?.selectedClientData?.Salutation || formData?.selectedClientData?.FirstName
                        ? t("dear")
                        : t("dearSirMadam")}{" "}
                      {getTranslatedSalutation(formData?.selectedClientData, t)},
                    </p>
                  </div>
                </>
              )}

              {/* Page content */}
              {sectionKeys.map(renderSection)}

              {/* Footer: only on last page */}
              {pageIndex === allPages.length - 1 && (
                <>
                  <p className="fs-10 mb-2">{t("signedCopyReturn")}</p>
                  <p className="fs-10 mb-2">{t("greeting")}</p>

                  {/* Signature Block */}
                  <div className="mt-4 mb-2 fs-10">
                    <Row className="mt-3">
                      {/* Company Signature */}
                      <Col xs={12} md={6} className="text-left order-1 order-md-1">
                        {formData?.companySignature ? (
                          <>
                            <img
                              src={`data:image/png;base64,${formData.companySignature}`}
                              alt={t("companySignature")}
                              className="p-2 mt-2"
                              style={{
                                width: "150px",
                                height: "60px",
                                objectFit: "contain",
                              }}
                            />
                            <p className="fs-10 mb-0">
                              <strong>
                                {t("dateOfSigning")}:{" "}
                                {formData?.companySignatureDate
                                  ? moment(
                                    formData.companySignatureDate
                                  ).format("DD-MM-YYYY")
                                  : moment().format("DD-MM-YYYY")}
                              </strong>
                            </p>
                          </>
                        ) : (
                          <>
                            <h6>{t("companySignature")}</h6>
                            <div
                              style={{
                                paddingTop: "20px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              <p className="mb-2">
                                _______________________________
                              </p>
                              <p className="mb-2">
                                <small>{t("noSignatureAvailable")}</small>
                              </p>
                            </div>
                          </>
                        )}
                      </Col>

                      {/* Company Address */}
                      <Col xs={12} md={6} className="text-left order-2 order-md-3">
                        <p className="fs-10 mt-2 mb-0">
                          {formData?.adminName || "--"},
                          <br />
                          {formData?.settings?.OrganizationName || "--"}
                          <br />
                          {formData?.locationDetails?.City || "--"}
                        </p>
                      </Col>

                      {/* Client Signature */}
                      <Col xs={12} md={6} className="text-left order-3 order-md-2 mt-3">
                        {formData?.clientSignatureImage ? (
                          <>
                            <img
                              src={formData.clientSignatureImage}
                              alt={t("customerSignature")}
                              className="p-2 mt-2"
                              style={{
                                width: "150px",
                                height: "60px",
                                objectFit: "contain",
                              }}
                            />
                            {formData?.clientSignatureDate && (
                              <p className="fs-10 mb-0">
                                <strong>
                                  {t("dateOfSigning")}:{" "}
                                  {formData.clientSignatureDate}
                                </strong>
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <h6>{t("customerSignature")}</h6>
                            <div
                              style={{
                                paddingTop: "20px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              <p className="mb-2">
                                _______________________________
                              </p>
                              <p className="mb-2">
                                <small>
                                  {t("signatureAndDateWillAppearHere")}
                                </small>
                              </p>
                            </div>
                          </>
                        )}
                      </Col>

                      {/* Client Address */}
                      <Col xs={12} md={6} className="text-left order-4 order-md-4">
                        <p className="fs-10 mt-2 mb-0">
                          {getTranslatedSalutation(formData?.selectedClientData, t)}
                          <br />
                          {formData?.selectedClientData?.Name || ""}
                          <br />
                          {formData?.selectedClientData?.City || ""}
                        </p>
                      </Col>
                    </Row>
                  </div>

                  {/* Footer details */}
                  <div
                    className="fs-10 text-end mt-5 text-muted"
                    style={{ fontSize: "12px", opacity: 0.7 }}
                  >
                    <p className="mb-0">
                      {/* NL footer */}
                      {!finLabels.showIfsc && (
                        <>
                          <strong>{t("iban")}:</strong> {formData?.locationDetails?.Iban ?? "--"}
                          <br />
                          <strong>{t("gAccount")}:</strong> {formData?.locationDetails?.GAccount ?? "--"}
                          <br />
                          <strong>{t("chamberOfCommerce")}:</strong> {formData?.locationDetails?.KvKDetails ?? "--"}
                          <br />
                          <strong>{t("vatNumber")}:</strong> {formData?.locationDetails?.BTWDetails ?? "--"}
                        </>
                      )}
                      {/* IN footer */}
                      {finLabels.showIfsc && (
                        <>
                          <strong>{t(finLabels.account)}:</strong> {formData?.locationDetails?.AccountNumber ?? "--"}
                          <br />
                          <strong>{t(finLabels.ifsc)}:</strong> {formData?.locationDetails?.IfscCode ?? "--"}
                          <br />
                          <strong>{t(finLabels.gst)}:</strong> {formData?.locationDetails?.GstDetails ?? "--"}
                        </>
                      )}
                    </p>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      <EditableSectionModal
        initialContent={editorValue}
        show={editorVisible}
        onClose={handleCloseEditor}
        onSave={handleSaveEditor}
        label={sectionMap[editingKey]?.label}
      />

      <hr className="m-0"></hr>
      <Card className="mt-2 no-shadow">
        <Card.Body className="p-2">
          <Row className="justify-content-between align-items-center">
            <Col md>
              <h5 className="mb-2 mb-md-0"></h5>
            </Col>
            <Col xs="auto">
              <IconButton
                size="sm"
                icon="eye"
                className="me-1 mb-2 mb-sm-0"
                iconClassName="me-1"
                onClick={() => handlePreviewPDF(invoiceRef, setBorderVisible)}
                variant="success"
              >
                {t("preview")}
              </IconButton>

              <IconButton
                size="sm"
                icon="file"
                className="me-1 mb-2 mb-sm-0"
                iconClassName="me-1"
                onClick={() => handleSaveProposalDetails(false)} // Save only
                disabled={isLoading}
                variant="primary"
              >
                {t("save")}
              </IconButton>

              <IconButton
                size="sm"
                icon="paper-plane"
                className="me-1 mb-2 mb-sm-0"
                iconClassName="me-1"
                onClick={() => handleSaveProposalDetails(true)} // Save and Send
                disabled={isLoading}
                variant="info"
              >
                {t("saveAndSend")}
              </IconButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <EmailConfigRequiredModal
        show={showEmailConfigRequiredModal}
        onClose={() => setShowEmailConfigRequiredModal(false)}
        t={t}
        type={emailModalType}
      />
    </>
  );
};

export default TemplateRenderer;